<template>
    <div class="flex flex-col">
        <Tooltip>
            <template #trigger="tooltipProps">
                <button
                    class="rounded-t focus-visible:outline-offset-2 focus-visible:outline-2 focus-visible:outline-sky-500"
                    :class="[interactiveStyles]"
                    type="button"
                    v-bind="tooltipProps"
                    @click="$emit('changeTab')"
                >
                    <div
                        v-if="props.hasChanges"
                        class="size-2 absolute bg-red-700 rounded-full shadow-[0_0_0.0625rem_0.0625rem] shadow-red-50/50 right-1 top-1"
                    />

                    <StripedSolidBarsIcon v-if="icon === 'Striped-Solid-Bars'" class="size-4" />
                    <PhaseMinusPlusIcon v-if="icon === 'Edit-Phases'" class="size-4" />
                    <MoveLeftRightIcon v-if="icon === 'Move-Date'" class="size-4" />
                </button>
            </template>

            <template #text>
                <div v-if="icon === 'Striped-Solid-Bars'">
                    {{ $t('edit-entire-allocation') }}
                    <span v-if="props.hasChanges">{{ $t('tab-contains-unsaved-changes') }}</span>
                </div>
                <div v-if="icon === 'Edit-Phases'">
                    {{ $t('edit-phases') }}
                    <span v-if="props.hasChanges">{{ $t('tab-contains-unsaved-changes') }}</span>
                </div>
                <div v-if="icon === 'Move-Date'">
                    {{ $t('move-start-or-date') }}
                    <span v-if="props.hasChanges">{{ $t('tab-contains-unsaved-changes') }}</span>
                </div>
            </template>
        </Tooltip>

        <div v-if="status === 'Active'" class="rounded-t bg-gray-50 w-full">
            <slot />
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import MoveLeftRightIcon from '../../Icon/MoveLeftRightIcon.vue';
import PhaseMinusPlusIcon from '../../Icon/PhaseMinusPlusIcon.vue';
import StripedSolidBarsIcon from '../../Icon/StripedSolidBarsIcon.vue';
import Tooltip from '../../Tooltip/Tooltip.vue';

type AllocationEditTabNavigationItemProps = {
    icon: 'Striped-Solid-Bars' | 'Edit-Phases' | 'Move-Date';
    status: 'Active' | 'Default';
    hasChanges: boolean;
};

const props = defineProps<AllocationEditTabNavigationItemProps>();

const interactiveStyles = computed(() => {
    switch (props.status) {
        case 'Active':
            return 'text-gray-600 bg-gray-50 p-1.5 mt-1.5 inverted-radius inverted-radius-active';

        case 'Default':
        default:
            return `
                text-gray-200 bg-gray-500 px-1.5 py-[0.3125rem] mt-2 inverted-radius
                hover:text-gray-600 hover:bg-gray-50 hover:p-1.5 hover:mt-1.5
                focus-visible:text-gray-600 focus-visible:bg-gray-50 focus-visible:p-1.5 focus-visible:mt-1.5
            `;
    }
});

defineEmits<{
    (e: 'changeTab'): void;
}>();
</script>

<style scoped>
/* Based on: https://codepen.io/t_afif/pen/zYJVLRQ */
.inverted-radius,
.inverted-radius {
    position: relative;
}

.inverted-radius::before,
.inverted-radius::after {
    position: absolute;
    bottom: 0;
    width: 0.25rem;
    height: 0.25rem;
    content: '';
}

.inverted-radius::before {
    left: -0.25rem;
}

.inverted-radius::after {
    right: -0.25rem;
}

.inverted-radius-active::before,
.inverted-radius:hover::before,
.inverted-radius:focus-visible::before {
    background: radial-gradient(102% 102% at top left, transparent 98%, var(--color-gray-50));
}

.inverted-radius-active::after,
.inverted-radius:hover::after,
.inverted-radius:focus-visible::after {
    background: radial-gradient(102% 102% at top right, transparent 98%, var(--color-gray-50));
}
</style>
